import { Content, ContentFixed, ContentScroll } from "../../common/Content";
import styles from "./PlanningWeek.module.scss";
import { HeaderMenu } from "../../common/HeaderMenu";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router";
import { apiGetEvents } from "../../api/ApiEvents";
import { apiGetContext } from "../../api/ApiApp";
import { apiGetPlanningEvents, apiGetPlanningPrestations } from "../../api/ApiPlanning";
import PlanningDaySummary from "./PlanningDaySummary";
import PlanningDayList from "./PlanningDayList";
import PlanningMultiDay from "./PlanningMultiDay";
import WeekStrip from "./WeekStrip";
import SiteEventEditor from "../sites/SiteEventEditor";
import { FabButton } from "../../common/FabButton";
import { MODE_CREATE, MODE_EDIT } from "../../codes";
import useMediaQuery, { MOBILE_QUERY } from "../../util/useMediaQuery";
import { getPlanningWeekMode, setPlanningWeekMode } from "../../util/localStorage.service";
import { usePlanningRanges, usePlanningSites } from "./usePlanningData";
import { DAY_MODE, MULTI_DAY_MODE, planningWeekMode } from "./planningWeekMode";
import { addDays, buildDays, DATE_FORMAT, monthRange, monthsAround, startOfWeek, weekTitle } from "./planningData";

// The multi-day view covers a year before and after the week it opened on; it is recentred beyond that
const RANGE_WEEKS = 52;

// The data the route loader has just loaded is not loaded again when the page shows it
const LOADED_STALE_TIME = 30 * 1000;

const toPath = dayKey => "/planning/" + moment(dayKey, DATE_FORMAT).format("YYYY/MM/DD");

/**
 * Planning of the days, like the iOS calendar: a week strip at the top, and either one day as a list
 * ("Jour") or several days side by side (3 on phones, a week wider). Phones open on "Jour", larger screens
 * on several days, until the user picks a mode, which is then remembered.
 * The selected day is kept in the URL.
 */
const PlanningWeek = () => {
  const {day, month, year} = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  const today = useMemo(() => moment().startOf("day"), []);
  const todayKey = today.format(DATE_FORMAT);

  // The selected day is the URL's: the route loader preloads its data, so it shows complete
  const urlDay = moment(year + "-" + month + "-" + day, DATE_FORMAT, true);
  const selectedKey = urlDay.isValid() ? urlDay.format(DATE_FORMAT) : todayKey;

  const [savedMode, setSavedMode] = useState(getPlanningWeekMode);

  // Day that "Jour" waits for: the mode changes in the render where the URL shows that day and the route loader
  // has loaded it, so "Jour" never shows another day or an empty day first
  const [dayModeTarget, setDayModeTarget] = useState(null);

  const isDayLoaded = dayKey => {
    const range = {from: dayKey, to: dayKey};

    return queryClient.getQueryData(apiGetPlanningEvents(range).queryKey) !== undefined
      && queryClient.getQueryData(apiGetPlanningPrestations(range).queryKey) !== undefined;
  };

  if (dayModeTarget && dayModeTarget === selectedKey && isDayLoaded(dayModeTarget)) {
    setDayModeTarget(null);
    setSavedMode(DAY_MODE);
  }
  const mode = planningWeekMode(savedMode, isMobile);

  const nbVisibleDays = isMobile ? 3 : 7;
  const siteColumnWidth = isMobile ? 110 : 240;

  // A week view starts on Monday; the 3-day view on the requested day
  const multiDayStart = useCallback(
    dayKey => nbVisibleDays === 7 ? startOfWeek(dayKey) : dayKey,
    [nbVisibleDays]
  );

  const [rangeCenter, setRangeCenter] = useState(() => startOfWeek(selectedKey));
  const [firstVisibleKey, setFirstVisibleKey] = useState(() => multiDayStart(selectedKey));
  const [scrollTarget, setScrollTarget] = useState(() => ({key: multiDayStart(selectedKey), smooth: false}));
  const [daySummary, setDaySummary] = useState({date: moment(), site: null, events: [], shown: false});
  const [eventEditor, setEventEditor] = useState({show: false, event: {}, mode: MODE_CREATE, site: {id: null}});

  const days = useMemo(() => buildDays(
    moment(rangeCenter, DATE_FORMAT).subtract(RANGE_WEEKS, "week"),
    (2 * RANGE_WEEKS + 1) * 7,
    today
  ), [rangeCenter, today]);

  const {sitesQuery, sites} = usePlanningSites();

  const contextQuery = useQuery(apiGetContext());
  const workersById = useMemo(
    () => new Map((contextQuery.data?.workers || []).map(worker => [worker.id, worker])),
    [contextQuery.data]
  );

  // The week shown in the strip: the selected day's, or in multi-day mode the first visible day's
  const anchorKey = mode === DAY_MODE ? selectedKey : firstVisibleKey;
  const stripDays = useMemo(() => buildDays(moment(startOfWeek(anchorKey), DATE_FORMAT), 7, today), [anchorKey, today]);

  const visibleKeys = useMemo(() => {
    if (mode === DAY_MODE) {
      return null;
    }

    return new Set(Array.from({length: nbVisibleDays}, (_, i) => addDays(firstVisibleKey, i)));
  }, [mode, firstVisibleKey, nbVisibleDays]);

  // "Jour" loads only the selected day; several days load month by month around the visible days
  const ranges = useMemo(() => {
    if (mode === DAY_MODE) {
      return [{from: selectedKey, to: selectedKey}];
    }

    const first = days.findIndex(d => d.key === firstVisibleKey);

    return first < 0 ? [] : monthsAround(days, first, first + nbVisibleDays - 1).map(monthRange);
  }, [mode, selectedKey, days, firstVisibleKey, nbVisibleDays]);

  // "Jour" shows only what the route loader has loaded. Several days load their months themselves: the loader
  // does not run when the mode changes nor when scrolling, and after a reload in "Jour" it has loaded only the day
  const {events, prestations, index} = usePlanningRanges(ranges, mode !== DAY_MODE, LOADED_STALE_TIME);

  const goToDay = useCallback(dayKey => navigate(toPath(dayKey), {replace: true}), [navigate]);

  const scrollToDay = useCallback(dayKey => {
    const target = multiDayStart(dayKey);

    if (target < days[0].key || target > days[days.length - 1].key) {
      setRangeCenter(startOfWeek(target));
      setScrollTarget({key: target, smooth: false});
    } else {
      setScrollTarget({key: target, smooth: true});
    }
  }, [multiDayStart, days]);

  const changeMode = newMode => {
    if (newMode === mode) {
      return;
    }

    // Saved first: the route loader reads it to know what to preload
    setPlanningWeekMode(newMode);

    if (newMode === DAY_MODE) {
      // Back on the selected day while it is on screen, else on the first visible day
      const dayKey = visibleKeys?.has(selectedKey) ? selectedKey : firstVisibleKey;

      setDayModeTarget(dayKey);
      goToDay(dayKey);
    } else {
      setSavedMode(newMode);
      setFirstVisibleKey(multiDayStart(selectedKey));
      setScrollTarget({key: multiDayStart(selectedKey), smooth: false});
    }
  };

  // In multi-day mode, a day picked in the week strip or with "Aujourd'hui" also becomes the selected day
  const pickDay = dayKey => {
    goToDay(dayKey);

    if (mode !== DAY_MODE) {
      scrollToDay(dayKey);
    }
  };

  const moveBy = nbDays => mode === DAY_MODE
    ? goToDay(addDays(selectedKey, nbDays))
    : scrollToDay(addDays(firstVisibleKey, nbDays));

  const showDaySummary = useCallback((date, site) => {
    return queryClient
      .fetchQuery(apiGetEvents({date: date.format(DATE_FORMAT)}))
      .then(data => setDaySummary({date, site, events: data, shown: true}));
  }, [queryClient]);

  const openDay = useCallback(
    (dayKey, site) => showDaySummary(moment(dayKey, DATE_FORMAT), site),
    [showDaySummary]
  );

  // The queries of "Jour" are disabled, so not refetched by the invalidation: the selected day is loaded again
  const refreshEvents = () => Promise.all([
    queryClient.invalidateQueries({queryKey: ["planning_events"]}),
    mode === DAY_MODE ? queryClient.fetchQuery(apiGetPlanningEvents({from: selectedKey, to: selectedKey})) : null
  ]);

  if (!sitesQuery.isSuccess) {
    return null;
  }

  return <Content>
    <ContentFixed>
      <HeaderMenu title={"Planning"}/>
    </ContentFixed>

    <ContentScroll paddingTop className={styles.weekScroll}>
      <div className={styles.page}>
        <div className={styles.navActions}>
          <button type="button"
                  className={clsx(styles.navBtn, styles.todayBtn)}
                  onClick={() => pickDay(todayKey)}>
            Aujourd'hui
          </button>
          <button type="button"
                  className={clsx(styles.navBtn, styles.arrowBtn)}
                  onClick={() => moveBy(mode === DAY_MODE ? -1 : -nbVisibleDays)}>
            <span className="material-icons">chevron_left</span>
          </button>
          <button type="button"
                  className={clsx(styles.navBtn, styles.arrowBtn)}
                  onClick={() => moveBy(mode === DAY_MODE ? 1 : nbVisibleDays)}>
            <span className="material-icons">chevron_right</span>
          </button>

          <h4 className={styles.title}>{weekTitle(stripDays[0].key, stripDays[6].key)}</h4>

          <div className={styles.modeSwitch}>
            <button type="button"
                    className={clsx(mode === DAY_MODE && styles.active)}
                    onClick={() => changeMode(DAY_MODE)}>
              Jour
            </button>
            <button type="button"
                    className={clsx(mode === MULTI_DAY_MODE && styles.active)}
                    onClick={() => changeMode(MULTI_DAY_MODE)}>
              {nbVisibleDays === 7 ? "Semaine" : nbVisibleDays + " jours"}
            </button>
          </div>
        </div>

        <WeekStrip days={stripDays}
                   selectedKey={mode === DAY_MODE ? selectedKey : null}
                   visibleKeys={visibleKeys}
                   onSelect={pickDay}
                   onPreviousWeek={() => moveBy(-7)}
                   onNextWeek={() => moveBy(7)}/>

        <div className={styles.view}>
          {mode === DAY_MODE
            ? <PlanningDayList dayKey={selectedKey}
                               sites={sites}
                               events={events}
                               prestations={prestations}
                               workersById={workersById}
                               openEvent={(event, site) => setEventEditor({
                                 show: true,
                                 event,
                                 mode: MODE_EDIT,
                                 site: site || {id: null}
                               })}
                               onPreviousDay={() => goToDay(addDays(selectedKey, -1))}
                               onNextDay={() => goToDay(addDays(selectedKey, 1))}/>
            : <PlanningMultiDay days={days}
                                sites={sites}
                                index={index}
                                workersById={workersById}
                                siteColumnWidth={siteColumnWidth}
                                nbVisibleDays={nbVisibleDays}
                                snap={isMobile}
                                scrollTarget={scrollTarget}
                                onFirstVisibleDay={setFirstVisibleKey}
                                openDay={openDay}/>}
        </div>
      </div>

      <PlanningDaySummary
        daySummary={daySummary}
        setDaySummary={setDaySummary}
        onEventSubmitted={() => {
          return Promise.all([
            refreshEvents(),
            showDaySummary(daySummary.date, daySummary.site)
          ]);
        }}/>
    </ContentScroll>

    <FabButton onClick={() => setEventEditor({
      show: true,
      event: {date: selectedKey},
      mode: MODE_CREATE,
      // An event without site, like those of the "Journée" card
      site: {id: null}
    })}/>

    <SiteEventEditor
      show={eventEditor.show}
      hide={() => setEventEditor(prev => ({...prev, show: false}))}
      mode={eventEditor.mode}
      site={eventEditor.site}
      event={eventEditor.event}
      onSubmit={refreshEvents}
      onDelete={refreshEvents}/>
  </Content>;
};

export default PlanningWeek;
